import React from "react";
import { Link } from "react-router-dom";
import SuggestedConnections from "../feed_page/SuggestedConnections";
import SignedOutPrompt from "../SignedOutPrompt";
import { isAccountVerified } from "../utils";
import { useUserRealtime } from "../../context/UserRealtimeContext";
import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";

/**
 * @param {Object} props
 * @param {number} [props.size]
 * @returns {JSX.Element}
 */
function CompassIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M16.24 7.76l-2.12 6.36-6.36 2.12 2.12-6.36z" />
    </svg>
  );
}

/**
 * @param {Object} props
 * @param {number} [props.size]
 * @returns {JSX.Element}
 */
function PersonAddIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="10" cy="8" r="3.5" />
      <path d="M3.5 20v-1.5a6.5 6.5 0 0113 0V20" />
      <path d="M19 8v6M16 11h6" />
    </svg>
  );
}

/**
 * @param {Object} props
 * @param {number} [props.size]
 * @returns {JSX.Element}
 */
function ChatIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 5h16v11H8l-4 4z" />
    </svg>
  );
}

/**
 * @param {Object} props
 * @param {number} [props.size]
 * @returns {JSX.Element}
 */
function BuildingIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="4" y="3" width="16" height="18" rx="1" />
      <path d="M9 8h2M9 12h2M9 16h2M13 8h2M13 12h2M13 16h2" />
    </svg>
  );
}

/**
 * @param {Object} props
 * @param {number} [props.size]
 * @returns {JSX.Element}
 */
function ArrowRightIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

/**
 * Network shortcut card.
 * @param {Object} props
 * @param {string} props.to
 * @param {Function} props.Icon
 * @param {string} props.title
 * @param {string} props.description
 * @param {number} [props.badge]
 * @returns {JSX.Element}
 */
function ShortcutCard({ to, Icon, title, description, badge }) {
  return (
    <Link
      to={to}
      className={cn(
        "group flex flex-col gap-3 rounded-xl border border-ui-border bg-ui-card p-5 shadow-shell-sm",
        "transition hover:border-accent hover:shadow-shell-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-tag-accent text-tag-accent-fg">
          <Icon size={20} />
        </div>
        {badge != null && badge > 0 ? (
          <Badge variant="default">{badge > 9 ? "9+" : badge}</Badge>
        ) : null}
      </div>
      <div>
        <div className="mb-1 text-sm font-semibold text-text-primary">{title}</div>
        <div className="text-sm leading-snug text-text-muted">{description}</div>
      </div>
      <div className="mt-auto flex items-center gap-1 text-xs font-semibold text-accent">
        Open
        <ArrowRightIcon size={13} />
      </div>
    </Link>
  );
}

/**
 * Network hub: shortcuts, pending badges, and suggested connections.
 * @param {Object} props
 * @param {number} props.userId
 * @param {object|null} props.user
 * @returns {JSX.Element}
 */
function NetworkPage({ userId, user }) {
  const { pendingConnectionCount, pendingAffiliationCount } = useUserRealtime();

  const pendingAffiliationsCount = pendingAffiliationCount;
  const pendingConnectionsCount = pendingConnectionCount;

  if (!userId) {
    return (
      <SignedOutPrompt
        title="Your Assembly network"
        description="Sign in to find pastors and organizations, send connection requests, and grow your ministry circle."
      />
    );
  }

  if (user && !isAccountVerified(user)) {
    return (
      <SignedOutPrompt
        variant="restricted"
        title="Your Assembly network"
        description="Your account must be verified before you can use the network. Until then, you can browse Explore and manage your profile."
      />
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-8 px-4 py-6 pb-12">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-medium text-text-primary sm:text-3xl">Network</h1>
          <p className="mt-1 text-sm text-text-muted">
            Discover people and churches, respond to invitations, and keep messaging close at hand.
          </p>
        </div>
        {/* {pendingTotal > 0 ? (
          <div className="flex flex-wrap gap-2">
            {pendingConnectionsCount > 0 ? (
              <Button asChild size="sm">
                <Link to="/network/connections?tab=received">
                  Connection requests
                  <Badge variant="secondary" className="ml-2 bg-ui-card text-accent">
                    {pendingConnectionsCount > 9 ? "9+" : pendingConnectionsCount}
                  </Badge>
                </Link>
              </Button>
            ) : null}
            {pendingAffiliationsCount > 0 ? (
              <Button asChild size="sm" variant="outline">
                <Link to="/network/affiliations?tab=pending">
                  Organization invites
                  <Badge variant="accent" className="ml-2">
                    {pendingAffiliationsCount > 9 ? "9+" : pendingAffiliationsCount}
                  </Badge>
                </Link>
              </Button>
            ) : null}
          </div>
        ) : null} */}
      </div>

      <section>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-muted">Shortcuts</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <ShortcutCard
            to="/explore"
            Icon={CompassIcon}
            title="Explore"
            description="Search and filter pastors and organizations on the map or in list view."
          />
          <ShortcutCard
            to="/network/connections"
            Icon={PersonAddIcon}
            title="Manage Connections"
            description="View connections and respond to invitations."
            badge={pendingConnectionsCount}
          />
            <ShortcutCard
              to="/network/affiliations"
              Icon={BuildingIcon}
              title="Manage Affiliations"
              description="Manage invites and ties between your profile and organizations."
              badge={pendingAffiliationsCount}
            />
          <ShortcutCard
            to="/messaging"
            Icon={ChatIcon}
            title="Messages"
            description="Continue conversations with people in your network."
          />
        </div>
      </section>

      <section>
        <div className="mb-4">
          <h2 className="font-display text-lg font-medium text-text-primary">Suggested for you</h2>
          <p className="mt-1 text-sm text-text-muted">
            Profiles you might want to connect with next.
          </p>
        </div>
        <SuggestedConnections
          userId={userId}
          user={user}
          alwaysVisible
          title=""
          showProfileLink={false}
          limit={8}
          variant="list"
        />
      </section>
    </div>
  );
}

export default NetworkPage;
